import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Check, Star, Crown, Loader2 } from 'lucide-react';
import AuthModal from './AuthModal';
import SubscriptionRegistrationModal from './SubscriptionRegistrationModal';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const Pricing = () => {
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const { isAuthenticated, token } = useAuth();
  const { toast } = useToast();

  // Fallback plans in case API fails
  const fallbackPlans = {
    seekers: {
      name: "🌟 The Seekers Path",
      description: "Complete access to reclaim your ancestral legacy",
      tagline: "Begin your journey to ancestral sovereignty with full access to all TribalRoot.AI features.",
      monthly_price: 9.99,
      yearly_price: 119.88,
      activation_fee: 0,
      features: [
        "Full access to AI Ancestry Analysis ✓",
        "Unlimited Crest Generator use ✓",
        "Access to The Guru (AI Guide) ✓",
        "Healing & Herbal Remedy Generator ✓",
        "Full Language Restoration Tool ✓",
        "Interactive Treaty & Tribal Rights Education ✓"
      ]
    },
    crown: {
      name: "👑 The Crown Subscription",
      description: "Ultimate sovereignty with official tribal documentation",
      tagline: "Crown yourself with complete tribal sovereignty and official recognition.",
      monthly_price: 9.99,
      yearly_price: 119.88,
      activation_fee: 129.99,
      features: [
        "Everything from The Seekers Path ✓",
        "Full access to AI Ancestry Analysis ✓",
        "Official Genealogy Paperwork via Email ✓",
        "Private ID Application via Email ✓",
        "Priority Document Processing ✓"
      ]
    }
  };

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const plansResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/plans`);
      
      if (!plansResponse.ok) {
        throw new Error(`API error: ${plansResponse.status}`);
      }
      
      const plans = await plansResponse.json();
      
      // Validate that we got valid data
      if (!plans || Object.keys(plans).length === 0) {
        throw new Error('No plans returned from API');
      }
      
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      // Use fallback plans if API fails
      setSubscriptionPlans(fallbackPlans);
      toast({
        variant: "destructive",
        title: "Connection Issue",
        description: "Using cached pricing data. Please refresh the page."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscription = async (tier) => {
    if (!isAuthenticated) {
      // Show registration modal for new users
      setSelectedTier(tier);
      setShowRegistrationModal(true);
      return;
    }

    // User is authenticated, proceed with checkout
    try {
      setProcessingPayment(tier);
      
      // Get current URL
      const currentUrl = window.location.origin;

      const requestBody = {
        subscription_tier: tier,
        billing_interval: 'monthly', // Default to monthly
        origin_url: currentUrl
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Subscription error:', error);
      setProcessingPayment(null);
      
      toast({
        variant: "destructive",
        title: "Subscription Failed",
        description: error.message || "Failed to start subscription process. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">Loading pricing...</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-white" data-section="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Choose Your Path to Ancestral Sovereignty
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Select your subscription to unlock the full power of TribalRoot.AI and reclaim your ancestral legacy
          </p>
        </div>

        {/* Pricing Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {Object.entries(subscriptionPlans).map(([tier, plan]) => (
            <Card key={tier} className={`relative bg-white shadow-lg border-0 overflow-hidden ${
              tier === 'crown' ? 'transform scale-105 ring-2 ring-amber-400' : ''
            }`}>
              {tier === 'crown' && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-center py-2 text-sm font-medium">
                  ⭐ RECOMMENDED
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className={`w-16 h-16 ${
                  tier === 'seekers' 
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600' 
                    : 'bg-gradient-to-br from-amber-500 to-amber-600'
                } rounded-full flex items-center justify-center mx-auto mb-4 text-white`}>
                  {tier === 'seekers' ? <Star className="h-8 w-8" /> : <Crown className="h-8 w-8" />}
                </div>
                
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </CardTitle>
                
                <p className="text-sm text-gray-600 mb-4">
                  {plan.description}
                </p>

                <div className="space-y-2">
                  {tier === 'crown' && plan.activation_fee > 0 && (
                    <div className="text-lg text-amber-600 font-medium">
                      💎 ${plan.activation_fee || 129.99} Initial
                      <div className="text-xs text-gray-500">
                        One-Time Setup Payment
                      </div>
                    </div>
                  )}
                  
                  <div className="text-3xl font-bold text-gray-900">
                    ${plan.monthly_price?.toFixed(2) || '9.99'}
                    <span className="text-lg text-gray-500">/month</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="px-6 pb-6">
                <div className="space-y-2 mb-6">
                  {plan.features?.slice(0, 5).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                  {plan.features?.length > 5 && (
                    <div className="text-sm text-gray-500 text-center pt-2">
                      +{plan.features.length - 5} more features
                    </div>
                  )}
                </div>

                {plan.tagline && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm italic text-gray-700 text-center">&quot;{plan.tagline}&quot;</p>
                  </div>
                )}

                <Button 
                  className={`w-full ${
                    tier === 'seekers' 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                      : 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white'
                  }`}
                  onClick={() => handleSubscription(tier)}
                  disabled={processingPayment === tier}
                >
                  {processingPayment === tier ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    tier === 'seekers' ? 'Activate Seekers Path' : 'Activate Crown Plan'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action section removed per user request */}

        {/* Subscription Registration Modal */}
        <SubscriptionRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => {
            setShowRegistrationModal(false);
            setSelectedTier(null);
          }}
          subscriptionTier={selectedTier}
          subscriptionPlan={selectedTier ? subscriptionPlans[selectedTier] : null}
          onSubscriptionSuccess={() => {
            setShowRegistrationModal(false);
            setSelectedTier(null);
          }}
        />
      </div>
    </section>
  );
};

export default Pricing;